import { useState, useEffect, useMemo } from 'react';
import WholesaleLayout, { useWholesaleAuth } from './WholesaleLayout';
import { getWholesaleProducts, uploadWholesaleCatalogue, uploadWholesaleProductImage } from '../../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

function StockIndicator({ stock }) {
  if (stock === 0 || stock === undefined || stock === null) return <div style={{ fontSize:10, letterSpacing:1, fontWeight:700, color:'#C41E2A', textTransform:'uppercase', marginTop:4 }}>OUT OF STOCK</div>;
  if (stock <= 5) return <div style={{ fontSize:10, color:'#E65100', marginTop:4 }}>LOW STOCK — {stock} left</div>;
  return <div style={{ fontSize:10, color:'#888', marginTop:4 }}>{stock} available</div>;
}

export default function WholesaleAdminProducts() {
  const user = useWholesaleAuth('admin');
  const [products, setProducts] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => { if (user) getWholesaleProducts().then(r => setProducts(r.data)).catch(() => {}); };
  useEffect(load, [user]);

  const grouped = useMemo(() => {
    const map = {};
    products.forEach(p => { const k = p.model || 'Other'; if (!map[k]) map[k] = []; map[k].push(p); });
    return map;
  }, [products]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await uploadWholesaleCatalogue(fd);
      alert(`Uploaded ${res.data.count} products`);
      load(); setShowUpload(false);
    } catch (err) {
      const d = err?.response?.data;
      const detail = d?.detail;
      const msg = typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map(e => e.msg || JSON.stringify(e)).join('; ') : (err?.message || JSON.stringify(d) || 'Upload failed'));
      alert(msg);
    }
    setUploading(false);
  };

  const handleImageUpload = async (pid, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData(); fd.append('file', file);
      await uploadWholesaleProductImage(pid, fd);
      load();
    } catch (err) { alert(err?.response?.data?.detail || err?.message || 'Image upload failed'); }
  };

  return (
    <WholesaleLayout user={user} title="Product Catalogue">
      <div style={{ marginBottom:24, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div style={{ background:'#FFFDE7', borderLeft:'4px solid #FBC02D', padding:'12px 16px', fontSize:13, flex:1, marginRight:16 }}>
          Upload an Excel file (.xlsx) with columns: Sr No, Model Name, Color, Pictures (URL), MRP, Wholesale Price, ..., HSN (col J), Stock (col K). Embedded images will be extracted automatically. Click any product image area to upload an image individually.
        </div>
        <button onClick={() => setShowUpload(true)} data-testid="upload-catalogue-btn" style={btnPrimary}>Upload Catalogue</button>
      </div>

      {/* Upload modal */}
      {showUpload && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center' }} onClick={() => setShowUpload(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background:'#fff', borderRadius:4, padding:32, width:460, maxWidth:'90vw' }}>
            <div style={cardTitle}>Upload Product Catalogue</div>
            <input type="file" accept=".xlsx,.xls" onChange={handleUpload} disabled={uploading} style={{ marginTop:16 }} data-testid="catalogue-file-input" />
            {uploading && <div style={{ marginTop:12, fontSize:13, color:'#888' }}>Uploading...</div>}
          </div>
        </div>
      )}

      {Object.entries(grouped).map(([model, variants]) => (
        <div key={model} style={{ marginBottom:40 }}>
          <h2 style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:20, fontWeight:700, letterSpacing:1, marginBottom:16 }}>{model}</h2>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:16 }}>
            {variants.map(p => (
              <div key={p.id} style={{ background:'#fff', border:'1px solid #E0DED8', borderRadius:4, overflow:'hidden' }}>
                <label style={{ display:'block', height:160, background:'#F7F6F2', cursor:'pointer', position:'relative', overflow:'hidden' }}>
                  <input type="file" accept="image/*" onChange={e => handleImageUpload(p.id, e)} style={{ display:'none' }} />
                  {p.image ? (
                    <img src={p.image.startsWith('http') ? p.image : `${API}${p.image}`} alt={p.color} style={{ width:'100%', height:'100%', objectFit:'contain' }} />
                  ) : (
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100%', color:'#bbb', fontSize:10, letterSpacing:1.5, textTransform:'uppercase' }}>Click to add image</div>
                  )}
                </label>
                <div style={{ padding:'12px 14px' }}>
                  <div style={{ fontWeight:600, fontSize:13 }}>{p.color}</div>
                  <div style={{ fontSize:11, color:'#888', marginTop:2 }}>MRP: ${p.mrp?.toLocaleString()} | WS: ${p.price?.toLocaleString()}</div>
                  <StockIndicator stock={p.stock} />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {products.length === 0 && <div style={{ textAlign:'center', padding:80, color:'#888', fontSize:14 }}>No products yet. Upload a catalogue to get started.</div>}
    </WholesaleLayout>
  );
}

const cardTitle = { fontFamily:"'Barlow Condensed',sans-serif", fontSize:16, fontWeight:700, letterSpacing:1.5, textTransform:'uppercase', marginBottom:8 };
const btnPrimary = { display:'inline-flex', alignItems:'center', gap:6, padding:'8px 16px', fontFamily:'Barlow', fontSize:12, fontWeight:600, letterSpacing:1, textTransform:'uppercase', borderRadius:2, cursor:'pointer', background:'#C41E2A', color:'#fff', border:'none' };
